import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import React, { Suspense, useEffect } from 'react';
import { onRouteChange } from './lib/analytics';
import { useAuth } from './contexts/AuthContext';
import { useShopify } from './contexts/ShopifyContext';
import { MainLayout } from './components/layout/MainLayout';
import { LoginPage } from './pages/Login/LoginPage';

// Lazy load heavy pages to reduce initial bundle size
const CustomerCustomizerPage = React.lazy(() => import('./pages/CustomerCustomizer').then(m => ({ default: m.CustomerCustomizerPage })));
const CatalogCustomizerPage = React.lazy(() => import('./pages/CatalogCustomizer').then(m => ({ default: m.CatalogCustomizerPage })));
const TemplateEditorPage = React.lazy(() => import('./pages/TemplateEditor/TemplateEditorPage').then(m => ({ default: m.TemplateEditorPage })));
const PersonalizationsPage = React.lazy(() => import('./pages/Personalizations/PersonalizationsPage').then(m => ({ default: m.PersonalizationsPage })));
const PersonalizationEditorPage = React.lazy(() => import('./pages/PersonalizationEditor/PersonalizationEditorPage').then(m => ({ default: m.PersonalizationEditorPage })));
const GroupsPage = React.lazy(() => import('./pages/Groups/GroupsPage').then(m => ({ default: m.GroupsPage })));
const CatalogsPage = React.lazy(() => import('./pages/Catalogs/CatalogsPage').then(m => ({ default: m.CatalogsPage })));
const ProductsPage = React.lazy(() => import('./pages/Products').then(m => ({ default: m.ProductsPage })));
const ProductEditorPage = React.lazy(() => import('./pages/ProductEditor/ProductEditorPage').then(m => ({ default: m.ProductEditorPage })));
const FullscreenEditorPage = React.lazy(() => import('./pages/ProductEditor/FullscreenEditorPage').then(m => ({ default: m.FullscreenEditorPage })));
const OrdersPage = React.lazy(() => import('./pages/Orders/OrdersPage').then(m => ({ default: m.OrdersPage })));
const SettingsPage = React.lazy(() => import('./pages/Settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const Gallery3DPage = React.lazy(() => import('./pages/Gallery3D/Gallery3DPage').then(m => ({ default: m.Gallery3DPage })));
const AugmentedRealityPage = React.lazy(() => import('./pages/AugmentedReality/AugmentedRealityPage').then(m => ({ default: m.AugmentedRealityPage })));
const SalesChannelsPage = React.lazy(() => import('./pages/SalesChannels').then(m => ({ default: m.SalesChannelsPage })));
const DiscountsPage = React.lazy(() => import('./pages/Discounts/DiscountsPage').then(m => ({ default: m.DiscountsPage })));
const MonitoringPage = React.lazy(() => import('./pages/Monitoring').then(m => ({ default: m.MonitoringPage })));
const LabPage = React.lazy(() => import('./pages/Lab3d/LabPage').then(m => ({ default: m.LabPage })));

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isEmbedded, isReady: shopifyReady, error: shopifyError } = useShopify();
  
  if (isLoading || !shopifyReady) {
    return (
      <div className="turbine-loading-screen">
        <div className="turbine-spinner" />
        <span>Carregando...</span>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    // In embedded mode: show error with retry, never redirect
    if (isEmbedded) {
      return (
        <div className="turbine-loading-screen">
          <p>{shopifyError || 'Falha na autenticação com Shopify'}</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: '8px 24px', cursor: 'pointer' }}>
            Tentar novamente
          </button>
        </div>
      );
    }
    // Standalone mode: try legacy login page
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Para o Clarity se o documento sair das rotas publicas (transicao SPA).
const AnalyticsRouteGuard: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => { onRouteChange(pathname); }, [pathname]);
  return null;
};

export const Router: React.FC = () => {
  return (
    <BrowserRouter>
      <AnalyticsRouteGuard />
      <Suspense fallback={null}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/customize/:productId" element={<CustomerCustomizerPage />} />
        <Route path="/customize-catalog/:catalogKey" element={<CatalogCustomizerPage mode="catalog" />} />
        <Route path="/customize-group/:groupKey" element={<CatalogCustomizerPage mode="group" />} />
        
        {/* Fullscreen editor - outside layout for true fullscreen */}
        <Route 
          path="/editor/:productId" 
          element={
            <ProtectedRoute>
              <FullscreenEditorPage />
            </ProtectedRoute>
          } 
        />

        {/* Protected routes with layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/products" replace />} />
          <Route path="templates" element={<TemplateEditorPage />} />
          <Route path="templates/:templateId" element={<TemplateEditorPage />} />
          <Route path="personalizations" element={<PersonalizationsPage />} />
          <Route path="personalizations/:personalizationId/edit" element={<PersonalizationEditorPage />} />
          <Route path="groups" element={<GroupsPage />} />
          <Route path="catalogs" element={<CatalogsPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="products/:productId" element={<ProductEditorPage />} />
          <Route path="discounts" element={<DiscountsPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="gallery-3d" element={<Gallery3DPage />} />
          <Route path="augmented-reality" element={<AugmentedRealityPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="sales-channels" element={<SalesChannelsPage />} />
          <Route path="monitoring" element={<MonitoringPage />} />
          <Route path="lab-3d" element={<LabPage />} />
          <Route path="lab-cristais" element={<Navigate to="/lab-3d" replace />} />
        </Route>
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
};
